import type { ReactNode } from "react";
import { MicroLabel } from "../ui/Text";

/**
 * The page masthead, and the one place the grid is deliberately broken.
 *
 * The title is set at display size against a 2px rule that draws itself on
 * mount. Metrics sit to its right on wide viewports and below it on narrow ones,
 * so the composition is asymmetric rather than a centred hero — the eye lands on
 * the title, then travels right along the rule to the figures.
 */

export function PageHeader({
  eyebrow,
  title,
  lede,
  metrics,
  actions,
  freshness,
}: {
  readonly eyebrow: string;
  readonly title: string;
  /** One sentence. Says what the page is for, or what is currently limiting it. */
  readonly lede?: ReactNode;
  readonly metrics?: ReactNode;
  readonly actions?: ReactNode;
  /** The age of the data below, for views that poll. See `ui/Freshness`. */
  readonly freshness?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-5 pt-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-2">
          <MicroLabel>{eyebrow}</MicroLabel>
          <h1 className="m-0 font-display text-display font-semibold text-ink">{title}</h1>
        </div>
        {metrics === undefined && freshness === undefined ? null : (
          <div className="flex flex-col gap-3 lg:items-end">
            {freshness}
            {metrics === undefined ? null : (
              /**
               * `items-start`, not `items-end`.
               *
               * A `Metric` is a three-row stack and its third row — the hint — is
               * optional, so bottom-aligning the blocks pushed the label and the
               * numeral of a hintless metric *down* to meet the others' baselines.
               * Which is the misalignment: on Depots, "Sites" sat a line lower than
               * "Timezones" beside it. Top-aligning locks the label row and the
               * numeral row across every metric and lets the hints hang at whatever
               * height they need, including when one of them wraps on a narrow
               * viewport.
               */
              <div className="flex flex-wrap items-start gap-x-10 gap-y-4 lg:justify-end">
                {metrics}
              </div>
            )}
          </div>
        )}
      </div>

      {/* The structural rule. Heavy weight, drawn from the left. */}
      <div className="rule-draw h-0.5 w-full bg-rule-strong" />

      {lede === undefined && actions === undefined ? null : (
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          {lede === undefined ? (
            <span />
          ) : (
            <p className="m-0 max-w-prose text-body text-ink-muted">{lede}</p>
          )}
          {actions === undefined ? null : (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          )}
        </div>
      )}
    </div>
  );
}
